import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, Signal, TemplateRef, viewChild } from '@angular/core';
import { createSelector, Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { CellTemplateContext, DatasetTableConfig, PreloaderColor, RouteModalService, UiButtonComponent, UiDatasetTableComponent, UiNoDataComponent, UiPreloaderComponent } from '@ui';
import { syncQueryParam } from '../../../../../../helpers/sync-query-param.helper';
import { features } from '../../../../../../consts/text.const';
import { VehicleGroupsActions } from '../../../../../../store/states/vehicle-groups/vehicle-groups.actions';
import { vehicleGroupsFeature } from '../../../../../../store/states/vehicle-groups/vehicle-groups.feature';
import { VehicleGroupsModel } from '../../../../../../store/models/vehicle-groups.model';
import { SharedModels } from '@share-models';
import { routerSegments } from '../../../../../../consts/routes';
import { Router } from '@angular/router';
import { createVehicleGroupsTableConfig } from './vehicle-groups-table.util';
import { openDialogByParams } from '../../../../../../consts/open-dialog-by-params.consts';
import { VehicleGroupsDialogService } from '../../../../../../services/vehicle-groups-dialog.service';
import { UiSearchComponent } from '../../../../../../shared/ui-kit/ui-search/ui-search.component';

@Component({
  selector: 'vehicle-groups',
  imports: [
    TranslatePipe,
    UiNoDataComponent,
    UiPreloaderComponent,
    UiSearchComponent,
    UiButtonComponent,
    UiDatasetTableComponent,
  ],
  templateUrl: './vehicle-groups.component.html',
  styleUrl: './vehicle-groups.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VehicleGroupsComponent implements OnInit {
  private readonly store$ = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly routeModalService = inject(RouteModalService);
  private readonly vehicleDialogService = inject(VehicleGroupsDialogService);

  protected readonly sharedCellTemplate =
    viewChild<TemplateRef<CellTemplateContext<VehicleGroupsModel.VehicleGroupListDocument>>>('sharedCell');

  public readonly loaderColor = PreloaderColor;
  public readonly initialEmpty = features.vehicleGroups.initialEmpty;
  public readonly noResults = features.vehicleGroups.noResults;
  public readonly viewStates = SharedModels.ViewState;

  protected search = syncQueryParam<string>('search');

  public readonly selectIsLoadingSig: Signal<boolean> =
    this.store$.selectSignal(createSelector(vehicleGroupsFeature.selectVehicleGroups, (state) => state.isLoading),);

  protected readonly selectIsJoiningVehicleGroupSig: Signal<boolean> =
    this.store$.selectSignal(vehicleGroupsFeature.selectIsJoiningVehicleGroup);

  protected readonly selectIsCreatingVehicleGroupSig: Signal<boolean> =
    this.store$.selectSignal(vehicleGroupsFeature.selectIsCreatingVehicleGroup);

  public readonly selectAllVehicleGroupsSig: Signal<VehicleGroupsModel.VehicleGroupListDocument[]> =
    this.store$.selectSignal(vehicleGroupsFeature.selectAllVehicleGroups);

  public readonly selectProcessingIdsSig: Signal<string[]> =
    this.store$.selectSignal(vehicleGroupsFeature.selectVehicleGroupProcessingIds);

  public readonly filteredGroups = computed(() => {
    const raw = this.search();
    const q = String(raw ?? '').trim().toLowerCase();
    const list = this.selectAllVehicleGroupsSig();
    if (!q) {
      return list;
    }
    return list.filter((g) => g.name.toLowerCase().includes(q));
  });

  public get hasActiveFilters(): boolean {
    return String(this.search() ?? '').trim().length > 0;
  }

  public readonly tableConfig =
    computed<DatasetTableConfig<VehicleGroupsModel.VehicleGroupListDocument>>(() => {
      const sharedTpl = this.sharedCellTemplate();
      return createVehicleGroupsTableConfig({
        processingIds: () => this.selectProcessingIdsSig(),
        templates: sharedTpl ? { sharedCell: sharedTpl } : undefined,
        onRowClick: (row) => {
          void this.router.navigate([
            routerSegments.database,
            routerSegments.vehicleGroups,
            row._id,
          ]);
        },
      });
    });

  public readonly viewState = computed(() => {
    if (this.filteredGroups().length > 0) {
      return SharedModels.ViewState.HAS_CONTENT;
    }
    return this.hasActiveFilters
      ? SharedModels.ViewState.NO_RESULTS
      : SharedModels.ViewState.INITIAL_EMPTY;
  });

  public ngOnInit(): void {
    this.store$.dispatch(VehicleGroupsActions.loadVehicleGroups());

    this.routeModalService.initModalListeners(
      [
        {
          queryParam: openDialogByParams.joinVehicleGroup,
          open: (params) => this.vehicleDialogService.openJoinDialog(params),
          onClose: () => {
            this.routeModalService.removeQueryParam([
              openDialogByParams.joinVehicleGroup,
              VehicleGroupsModel.JoinRouteQueryParam.InviteCode,
            ]);
          },
        },
      ],
      this.destroyRef,
    );
  }

  public join(): void {
    this.routeModalService.openModal(openDialogByParams.joinVehicleGroup);
  }

  public create(): void {
    this.vehicleDialogService.openCreate();
  }

  public onRowAction(event: VehicleGroupsModel.DatasetRowActionEvent): void {
    switch (event.actionId) {
      case VehicleGroupsModel.RowActionId.Share:
        this.vehicleDialogService.openShare(event.row._id, event.row.name, event.row.createdByOrgId);
        break;
      case VehicleGroupsModel.RowActionId.Edit:
        this.vehicleDialogService.openEdit(event.row._id, event.row.name);
        break;
      case VehicleGroupsModel.RowActionId.Delete:
        this.store$.dispatch(VehicleGroupsActions.requestDeleteVehicleGroup({ id: event.row._id, name: event.row.name }));
        break;
      default:
        console.log('VehicleGroupsComponent.onRowAction', event.actionId, event.row._id);
    }
  }
}
